import { useEffect, useState } from "react";
import { api, fmtEUR } from "../lib/api";
import { buildImgUrl } from "../lib/img";
import { Eye, ShoppingBag, TrendingUp, Package, AlertTriangle, BellRing } from "lucide-react";
import { Badge } from "../components/ui/badge";

function KPI({ label, value, sub, icon: Icon, color = "#7AC143" }) {
  return (
    <div className="bg-white border border-[#E5EAE5] rounded-2xl p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-wider text-[#4B634B]">{label}</span>
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-3xl font-black tracking-tighter text-[#1A2E1A]">{value}</div>
      {sub && <div className="text-xs text-[#4B634B] mt-1">{sub}</div>}
    </div>
  );
}

function ProductRow({ p, extra }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b last:border-0 border-[#F2F5F0]" data-testid={`stat-product-${p.id}`}>
      <img src={buildImgUrl(p.image_url)} alt={p.name} className="w-10 h-10 rounded-lg object-cover" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{p.name}</div>
        <div className="text-xs text-[#4B634B]">{p.category} · stock {p.stock}</div>
      </div>
      <div className="text-right text-sm font-bold text-[#1A2E1A] whitespace-nowrap">{extra}</div>
    </div>
  );
}

export default function StatsPanel() {
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);

  if (!s) return <div className="py-12 text-center text-[#4B634B]">Chargement des statistiques...</div>;

  return (
    <div className="space-y-8" data-testid="stats-panel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Chiffre d'affaires" value={fmtEUR(s.kpi.revenue_total)} sub={`${fmtEUR(s.kpi.revenue_30d)} sur 30j`} icon={TrendingUp} color="#7AC143" />
        <KPI label="Commandes" value={s.kpi.orders_total} sub={`${s.kpi.orders_30d} sur 30j`} icon={ShoppingBag} color="#FACC15" />
        <KPI label="Vues produits" value={s.kpi.total_views} sub={`${s.kpi.conversion_rate}% conversion`} icon={Eye} color="#003087" />
        <KPI label="Alertes stock" value={s.kpi.pending_stock_alerts} sub={`${s.out_of_stock.length} en rupture`} icon={BellRing} color="#EA4335" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E5EAE5] rounded-2xl p-6" data-testid="stat-top-sellers">
          <div className="flex items-center gap-2 mb-4">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#7AC143]">Top ventes</div>
          </div>
          {s.top_sellers.length === 0 ? (
            <p className="text-sm text-[#4B634B] py-6 text-center">Aucune vente pour l'instant.</p>
          ) : (
            s.top_sellers.map((p) => <ProductRow key={p.id} p={p} extra={`${p.qty_sold} · ${fmtEUR(p.revenue)}`} />)
          )}
        </div>

        <div className="bg-white border border-[#E5EAE5] rounded-2xl p-6" data-testid="stat-most-viewed">
          <div className="flex items-center gap-2 mb-4">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#003087]">Plus consultés</div>
          </div>
          {s.most_viewed.filter((p) => p.views > 0).length === 0 ? (
            <p className="text-sm text-[#4B634B] py-6 text-center">Aucune vue enregistrée.</p>
          ) : (
            s.most_viewed.filter((p) => p.views > 0).map((p) => <ProductRow key={p.id} p={p} extra={`${p.views} vues`} />)
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E5EAE5] rounded-2xl p-6" data-testid="stat-never-sold">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-4 h-4 text-[#4B634B]" />
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#4B634B]">Jamais vendus</div>
            <Badge variant="outline" className="ml-auto">{s.never_sold.length}</Badge>
          </div>
          {s.never_sold.length === 0 ? (
            <p className="text-sm text-[#4B634B] py-6 text-center">Tous vos produits ont au moins une vente. ✨</p>
          ) : (
            s.never_sold.map((p) => <ProductRow key={p.id} p={p} extra={`${p.views} vues`} />)
          )}
        </div>

        <div className="bg-white border border-[#E5EAE5] rounded-2xl p-6" data-testid="stat-stock-issues">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-[#EA4335]" />
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#EA4335]">Stock critique</div>
          </div>
          {s.out_of_stock.length === 0 && s.low_stock.length === 0 ? (
            <p className="text-sm text-[#4B634B] py-6 text-center">Tous les stocks sont OK. ✅</p>
          ) : (
            <>
              {s.out_of_stock.map((p) => <ProductRow key={p.id} p={p} extra={<Badge className="bg-red-500">Rupture</Badge>} />)}
              {s.low_stock.map((p) => <ProductRow key={p.id} p={p} extra={<Badge className="bg-[#FACC15] text-[#1A2E1A]">Bas: {p.stock}</Badge>} />)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
